import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckIcon, Loader2Icon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { GoldMedal } from './LifetimeSeal';
import { money } from './QuantityStepper';
import { SHIPPING_ESTIMATE_NOTE } from '../data/upsellFlows';
import type { UpsellStep, OrderSummary } from '../data/upsellFlows';
import type { ActiveConfirmation } from '../hooks/useUpsellFlow';
interface UpsellModalProps {
  step: UpsellStep | null;
  /** Shown after an accepted offer that confirms before checkout. */
  confirmation?: ActiveConfirmation | null;
  onContinue?: () => void;
  onFollowUp?: () => void;
  /** True while the customer is being routed to the Shopify cart. */
  routing: boolean;
  /** The chosen product has no Shopify link yet. */
  missingLink?: boolean;
  onAccept: () => void;
  /** The smaller offer shown on the same screen, when the step has one. */
  onAcceptAlternate?: () => void;
  onDecline: () => void;
  /** Close without choosing: stays on the page, nothing is added. */
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;

/** Coverage and shipping status for one side of the comparison. */
function OrderStatus({
  summary,
  highlight = false
}: {
  summary: OrderSummary;
  highlight?: boolean;
}) {
  return <ul className="mt-2 grid gap-1 text-[11px] font-bold uppercase leading-tight tracking-wide" data-id="element-1282" data-mp="djasb0xgstqv">
      <li className={`flex items-start gap-1.5 ${highlight ? 'text-[#ffe79a]' : 'text-steel-300'}`} data-id="element-1283" data-mp="3c3kbqq1hx0hl">
        <ShieldCheckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1284" data-mp="3h241z2n4gyfr" />
        {summary.coverage}
      </li>
      <li className={`flex items-start gap-1.5 ${summary.freeShipping ? highlight ? 'text-grit-500' : 'text-steel-300' : 'text-steel-500'}`} data-id="element-1285" data-mp="3lycahx6tdtdy">
        <TruckIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1286" data-mp="2tb8dofhvkld9" />
        {summary.freeShipping ? 'Free standard shipping' : 'Shipping at checkout · typ. ~$6.95*'}
      </li>
    </ul>;
}

/**
 * One negotiation step. Always shows what the customer has now, what the offer
 * is, the exact extra cost and the new total, with a visible way to say no.
 */
export function UpsellModal({
  step,
  confirmation = null,
  onContinue,
  onFollowUp,
  routing,
  missingLink,
  onAccept,
  onAcceptAlternate,
  onDecline,
  onClose
}: UpsellModalProps) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(step || confirmation);
  useEffect(() => {
    if (!open) return;
    acceptRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !routing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, routing, onClose]);
  return <AnimatePresence data-id="element-1287" data-mp="3sn2gb1e7d2wp">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/85 backdrop-blur-sm sm:items-center sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.16,
      ease
    }} role="dialog" aria-modal="true" aria-labelledby="upsell-heading" data-id="element-1288" data-mp="3npj4e84z927x">
          <motion.div key={step ? step.id : 'confirmation'} className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto border-2 gold-edge bg-ink-950 shadow-[0_0_48px_rgba(242,201,76,0.14)]" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-1289" data-mp="15g6f9d5sw8yc">
            <div className="gold-plate flex items-center justify-between gap-3 px-5 py-2.5" data-id="element-1290" data-mp="1odr3ci0qr3hw">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-ink-950" data-id="element-1291" data-mp="2c3ldusxl8fyz">
                {step ? step.plate : 'Offer claimed'}
              </p>
              <button type="button" onClick={onClose} disabled={routing} aria-label="Close" className="-mr-2 flex h-8 w-8 items-center justify-center text-ink-950 transition-opacity duration-150 ease-grit hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-950" data-id="element-1292" data-mp="s9a4uwccxtxs">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1293" data-mp="1b009aphktosr" />
              </button>
            </div>

            {!step && confirmation && <div className="p-5 sm:p-7" data-id="element-1294" data-mp="16zpuqofpxq2a">
                <div className="flex items-center gap-4" data-id="element-1295" data-mp="170sn70sow0kb">
                  <GoldMedal size={72} data-id="element-1296" data-mp="126etr3xl83ia" />
                  <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,2.75rem)] font-extrabold uppercase leading-[0.95] gold-text" data-id="element-1297" data-mp="x8mtnmmshb30">
                    {confirmation.headline}
                  </h2>
                </div>
                <p className="mt-4 text-[15px] font-semibold leading-relaxed text-steel-200" data-id="element-1298" data-mp="1x8f6zbndttax">{confirmation.body}</p>
                {missingLink && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1299" data-mp="228bbdbzzrmr0">
                    This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
                  </p>}
                <button ref={acceptRef} type="button" onClick={onContinue} disabled={routing} className="mt-6 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-1300" data-mp="2g6i124usegfl">
                  {routing ? <>
                      <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1301" data-mp="13uvzd3loj5jq" />
                      Taking you to checkout
                    </> : <>
                      {confirmation.cta}
                      <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1302" data-mp="18t7b5jvjbq4l" />
                    </>}
                </button>
                {confirmation.followUp && <button type="button" onClick={onFollowUp} disabled={routing} className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 underline-offset-4 transition-colors duration-150 ease-grit hover:text-grit-500 hover:underline disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1303" data-mp="1ijsgdq5prswh">
                    {confirmation.followUp.label}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1304" data-mp="28ub01uvteke6" />
                  </button>}
                <p className="mt-4 text-center text-[11px] text-steel-500" data-id="element-1305" data-mp="2dugl2s7kx8na">Taxes, if applicable, calculated at checkout.</p>
              </div>}

            {step && <div className="p-5 sm:p-7" data-id="element-1306" data-mp="240zury6nibod">
              <h2 id="upsell-heading" className="grit-headline text-[clamp(1.85rem,6.5vw,3rem)] font-extrabold uppercase leading-[0.95] text-offwhite" data-id="element-1307" data-mp="1q2i8m4we978k">
                {step.headline}
                <br data-id="element-1308" data-mp="1h3of4ned0wz8" />
                <span className="gold-text" data-id="element-1309" data-mp="1h4689b5q6v2x">{step.highlight}</span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-steel-200" data-id="element-1310" data-mp="1l4gpjjyyx7x3">{step.intro}</p>

              {/* Current vs. new, side by side even on phones: totals are the first thing read. */}
              <div className="mt-5 grid grid-cols-2 gap-2 sm:gap-3" data-id="element-1311" data-mp="3fgocaa14yihs">
                <div className="border border-white/12 bg-white/[0.03] p-3 sm:p-4" data-id="element-1312" data-mp="2395u4njq7ab8">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1313" data-mp="1y9t6w1oy0184">Your selection</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-1314" data-mp="1ti1dljeejhea">
                    {step.current.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-400" data-id="element-1315" data-mp="1khv0hymm8jfn">{step.current.detail}</p>
                  <OrderStatus summary={step.current} data-id="element-1316" data-mp="1fiigdln4pqgr" />
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1317" data-mp="1fl4njk63y3yg">
                    Current product total
                  </p>
                  <p className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-1318" data-mp="15m5p0uf23op6">
                    {money(step.current.total)}
                  </p>
                </div>
                <div className="border-2 border-grit-500 bg-grit-500/[0.07] p-3 sm:p-4" data-id="element-1319" data-mp="2zzy9tk2qz0ch">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1320" data-mp="38gp5wwvxjyrx">Upgrade</p>
                  <p className="mt-1.5 font-display text-base font-bold uppercase leading-tight text-offwhite sm:text-lg" data-id="element-1321" data-mp="3cgfqgiuv5io0">
                    {step.upgrade.label}
                  </p>
                  <p className="mt-0.5 text-[12px] leading-snug text-steel-300" data-id="element-1322" data-mp="3m7u03jgj3yuv">{step.upgrade.detail}</p>
                  <OrderStatus summary={step.upgrade} highlight data-id="element-1323" data-mp="2phtd9pzpzsg7" />
                  <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1324" data-mp="2timz8dtlqx6g">
                    New product total
                  </p>
                  <p className="font-display text-3xl font-extrabold leading-none text-grit-500" data-id="element-1325" data-mp="2yg50rj5aia4f">
                    {money(step.upgrade.total)}
                  </p>
                </div>
              </div>
              <p className="mt-2 bg-grit-500 px-4 py-2 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950" data-id="element-1326" data-mp="dr8xckehq207">
                {step.extra}
              </p>

              {step.economics && <dl className="mt-3 grid gap-1 border border-white/10 px-4 py-3 text-[13px]" data-id="element-1327" data-mp="24v4x4h9vdzgt">
                  <div className="flex justify-between gap-3 text-steel-300" data-id="element-1328" data-mp="24ss7rj1s8mtq">
                    <dt data-id="element-1329" data-mp="29mmukvcophx1">Product price difference</dt>
                    <dd className="font-semibold text-offwhite" data-id="element-1330" data-mp="2dnom0q0d0pog">{step.economics.productDiff}</dd>
                  </div>
                  <div className="flex justify-between gap-3 text-steel-300" data-id="element-1331" data-mp="1gyhb0bxd9yys">
                    <dt data-id="element-1332" data-mp="1lvh7bv4ezncp">Estimated shipping you no longer pay</dt>
                    <dd className="font-semibold text-offwhite" data-id="element-1333" data-mp="1ly3mogxjr75h">{step.economics.shippingSaved}</dd>
                  </div>
                  <div className="flex justify-between gap-3 border-t border-white/10 pt-1.5 font-bold uppercase tracking-wide text-grit-500" data-id="element-1334" data-mp="1uxgp4pn96vgp">
                    <dt data-id="element-1335" data-mp="31chkou8d9bmr">Expected difference, before tax</dt>
                    <dd data-id="element-1336" data-mp="36bkq4fs9djv9">{step.economics.result}</dd>
                  </div>
                </dl>}

              {step.valueLine && <dl className="mt-4 grid grid-cols-3 border border-white/10 text-center" data-id="element-1337" data-mp="3gf997v0emx77">
                  <div className="p-2.5" data-id="element-1338" data-mp="3bhhhp23o9d3j">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1339" data-mp="36pziojl7lml7">Normal value</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-steel-400 line-through" data-id="element-1340" data-mp="32pfnhlk74t1p">
                      {step.valueLine.normal}
                    </dd>
                  </div>
                  <div className="border-x border-white/10 p-2.5" data-id="element-1341" data-mp="2sq6w90gptsj1">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-1342" data-mp="2ssszinwkplvg">
                      {step.valueLine.todayLabel}
                    </dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-grit-500" data-id="element-1343" data-mp="2jrl4cepct15l">{step.valueLine.today}</dd>
                  </div>
                  <div className="p-2.5" data-id="element-1344" data-mp="hyolw6t46wbg">
                    <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1345" data-mp="hzq5izo74r4s">Free shipping</dt>
                    <dd className="mt-0.5 font-display text-lg font-bold text-offwhite" data-id="element-1346" data-mp="2bft4dxjbrque">{step.valueLine.shipping}</dd>
                  </div>
                </dl>}

              <div className="mt-4 flex items-start gap-4" data-id="element-1347" data-mp="22hqx509001yo">
                {step.lifetimeMedal && <div className="hidden shrink-0 sm:block" data-id="element-1348" data-mp="26ikmzz603qu7">
                    <GoldMedal size={78} data-id="element-1349" data-mp="2u73y0mgfqhpz" />
                  </div>}
                <div data-id="element-1350" data-mp="2z6qc0y4381zu">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-steel-400" data-id="element-1351" data-mp="2z6ymkjcqmv7v">You get</p>
                  <ul className="mt-2 grid gap-1.5 text-[14px] font-semibold text-steel-200" data-id="element-1352" data-mp="2l5vzc3a0bevd">
                    {step.includes.map(line => <li key={line} className="flex items-start gap-2" data-id="element-1353" data-mp="2q7v2bc1u49rs">
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1354" data-mp="19q7djf8holw5" />
                        {line}
                      </li>)}
                  </ul>
                </div>
              </div>

              <p className="mt-4 border-l-2 border-grit-500 pl-3 text-[13px] font-bold uppercase tracking-wide text-offwhite" data-id="element-1355" data-mp="128j63womn8ez">
                {step.clarification}
              </p>

              {missingLink && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-1356" data-mp="1b8o5pknkx2il">
                  This checkout link isn&apos;t connected yet. Please try again shortly or email support@gritvuegear.com.
                </p>}

              <button ref={acceptRef} type="button" onClick={onAccept} disabled={routing} className="mt-5 flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:text-2xl" data-id="element-1357" data-mp="x9of47giadjs">
                {routing ? <>
                    <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1358" data-mp="3887yo78psi3u" />
                    Taking you to checkout
                  </> : <>
                    {step.accept}
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1359" data-mp="3hxrj3hc2sk0t" />
                  </>}
              </button>
              {step.alternate && onAcceptAlternate && <div className="mt-5 border border-white/15 bg-white/[0.03] p-4" data-id="element-1360" data-mp="3rwyqlnw4pkg0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-1361" data-mp="3vx0sny5s0fvt">{step.alternate.eyebrow}</p>
                  <div className="mt-1.5 flex items-start justify-between gap-4" data-id="element-1362" data-mp="1xk8y9alesjaq">
                    <div data-id="element-1363" data-mp="21ksvee5zfmwv">
                      <p className="font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1364" data-mp="21m3yscvimsng">
                        {step.alternate.headline}
                      </p>
                      <p className="mt-1 text-[13px] leading-snug text-steel-300" data-id="element-1365" data-mp="1p88k8y7itxmr">{step.alternate.body}</p>
                      <OrderStatus summary={step.alternate.summary} highlight data-id="element-1366" data-mp="1kayol0tjgpqh" />
                    </div>
                    <div className="shrink-0 text-right" data-id="element-1367" data-mp="1g70q8sc1fiyh">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-1368" data-mp="2cybb5azp8xjs">New total</p>
                      <p className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-1369" data-mp="281ktwkc5rvws">
                        {money(step.alternate.summary.total)}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-[12px] font-bold uppercase tracking-wide text-steel-200" data-id="element-1370" data-mp="1z1xxkcyadgal">
                    {step.alternate.extra} · {step.alternate.clarification}
                  </p>
                  <button type="button" onClick={onAcceptAlternate} disabled={routing} className="mt-3 flex w-full items-center justify-center gap-2 border-2 border-grit-500 px-4 py-3 text-center font-display text-lg font-extrabold uppercase tracking-wide text-grit-500 transition-[background-color,color] duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1371" data-mp="nmsp0sbh4nj2">
                    {step.alternate.cta}
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1372" data-mp="b3ndl5dtz7h3" />
                  </button>
                </div>}
              <button type="button" onClick={onDecline} disabled={routing} className="mt-2.5 w-full border border-white/20 px-4 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-steel-300 transition-colors duration-150 ease-grit hover:border-white/40 hover:text-offwhite disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1373" data-mp="1b99i5za5orr">
                {step.decline}
              </button>
              <p className="mt-3 text-center text-[11px] leading-snug text-steel-500" data-id="element-1374" data-mp="3dwog5b506oil">
                {step.economics || !step.current.freeShipping ? SHIPPING_ESTIMATE_NOTE : 'Taxes, if applicable, calculated at checkout.'}
              </p>
            </div>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}